/**
 * Tipos e chamadas do cadastro de fornecedor.
 *
 * O contrato espelha `api/internal/modulos/fornecedores/dominio.go`. Fornecedor
 * é a mesma FORMA que cliente (identificação + contato + endereço) com o papel
 * de negócio invertido: vende PARA a loja, não compra dela. Por isso este
 * arquivo é irmão de `plataforma/clientes.ts` e não uma variação dele — sem
 * crediário, sem preferência de grade e sem os dois consentimentos de
 * marketing da LGPD: fornecedor não é titular de dado pessoal de consumo.
 *
 * `fonte` diz de onde o número saiu. `aguarda_compra` significa que o módulo
 * de compra (pedido, recebimento) não existe ainda — a tela mostra o texto de
 * `espera` em vez de desenhar zero, que pareceria medição.
 *
 * `documento` e `telefone` chegam CRUS. A máscara é da tela (ver
 * plataforma/mascaras.ts), pelo mesmo motivo de dinheiro chegar em centavos.
 *
 * E não existe `id`. A identidade que atravessa a fronteira é o `codigo`
 * (`F0001`), sequencial por loja — CONSTITUICAO.md §2.
 */
import { api } from './api'

export type TipoFornecedor = 'pf' | 'pj'
export type StatusFornecedor = 'ativo' | 'inativo'
export type Filtro = 'todos' | 'ativos' | 'com_prazo' | 'inativos'
export type Fonte = 'real' | 'aguarda_compra'
export type FormatoKPI = 'dinheiro' | 'inteiro'

export type KPI = {
  chave: string
  rotulo: string
  formato: FormatoKPI
  valor: number
  fonte: Fonte
  detalhe?: string
  espera?: string
}

export type ContagemFiltro = {
  chave: Filtro
  rotulo: string
  total: number
}

export type Bloco = {
  fonte: Fonte
  espera?: string
}

export type Pedido = {
  quando: string
  resumo: string
  total_centavos: number
}

export type BlocoCompras = Bloco & { linhas: Pedido[] }

export type LinhaFornecedor = {
  /** `F0001` — a identidade que o balcão dita ao telefone. */
  codigo: string
  nome: string
  nome_fantasia?: string
  tipo: TipoFornecedor
  tipo_rotulo: string
  documento?: string
  telefone?: string
  email?: string
  /** Nulo é "a combinar por pedido" (migração 0010) — não é zero. */
  prazo_pagamento_dias?: number
  status: StatusFornecedor
  status_rotulo: string
  criado_em: string
}

export type ListaDeFornecedores = {
  kpis: KPI[]
  filtros: ContagemFiltro[]
  filtro: Filtro
  busca?: string
  /** Declara, para a tabela inteira, que "último pedido" e "compras 12 meses"
   *  ainda não têm de onde sair — o módulo de compra não existe. */
  compras: Bloco
  linhas: LinhaFornecedor[]
  total: number
  /** Tamanho da base inteira — é o "N de M" do rodapé. */
  base: number
}

export type Contato = {
  telefone?: string
  email?: string
}

export type Endereco = {
  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  uf?: string
}

export type FichaFornecedor = {
  codigo: string
  nome: string
  nome_fantasia?: string
  tipo: TipoFornecedor
  tipo_rotulo: string
  documento?: string
  inscricao_estadual?: string
  contato: Contato
  endereco: Endereco
  prazo_pagamento_dias?: number
  observacao_interna?: string
  status: StatusFornecedor
  status_rotulo: string
  compras: BlocoCompras
  criado_em: string
  atualizado_em: string
}

export type PedidoFornecedor = {
  tipo: TipoFornecedor
  nome: string
  nome_fantasia?: string
  documento?: string
  inscricao_estadual?: string
  email?: string
  telefone?: string
  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  uf?: string
  prazo_pagamento_dias?: number
  observacao_interna?: string
  status?: StatusFornecedor
}

export function carregarFornecedores(
  busca: string,
  filtro: Filtro,
  sinal?: AbortSignal,
): Promise<ListaDeFornecedores> {
  const params = new URLSearchParams()
  if (busca) params.set('busca', busca)
  if (filtro && filtro !== 'todos') params.set('filtro', filtro)
  return api.get<ListaDeFornecedores>(`/fornecedores?${params}`, sinal)
}

export function carregarFichaFornecedor(
  codigo: string,
  sinal?: AbortSignal,
): Promise<FichaFornecedor> {
  return api.get<FichaFornecedor>(`/fornecedores/${encodeURIComponent(codigo)}`, sinal)
}

export function criarFornecedor(pedido: PedidoFornecedor): Promise<FichaFornecedor> {
  return api.post<FichaFornecedor>('/fornecedores', pedido)
}

export function salvarFornecedor(
  codigo: string,
  pedido: PedidoFornecedor,
): Promise<FichaFornecedor> {
  return api.put<FichaFornecedor>(`/fornecedores/${encodeURIComponent(codigo)}`, pedido)
}

/** Diz se o bloco tem dado de verdade. */
export function temDado(bloco: Bloco): boolean {
  return bloco.fonte === 'real'
}

/** Unidades federativas, para o seletor de UF — mesma lista de `clientes.ts`. */
export const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT',
  'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
]
